// @givmo/app-admin — company-documents (annual-report) presentation helpers.
//
// The company-reports endpoint (`GET /users/me/company/donations/reports`) is typed by the generated
// TanStack-Query option as `Array<StatisticsReportOut>`, but list endpoints across the platform share
// the `{ data, meta, links }` pagination envelope that the @givmo/api-client response middleware
// REWRITES to the bare `data` array when it detects that envelope. The tolerant read for that is the
// shared `extractList` from `@givmo/api-client/list` (the documents page calls it directly); this
// module keeps the report-specific presentation helpers, which reuse the shared `toDateOnly`.

import { toDateOnly } from '@givmo/api-client/list';
import type { StatisticsReportOut, StatisticsReportPayload } from '@givmo/api-client';

/**
 * The single status label the backend uses to mean "the PDF artifact is built and downloadable".
 * The download route 404-collapses missing / not-owned / not-yet-COMPLETED into one not-found, so a
 * download is ONLY valid when `status === 'Completed'`. This constant is the gate the Download button
 * keys off (and the test pins).
 */
export const COMPLETED_STATUS = 'Completed' as const;

/**
 * The status the backend stamps on a report whose render worker failed. A Failed report has no PDF
 * artifact and is NOT re-enqueued automatically, so it is a dead-end without a retry affordance.
 */
export const FAILED_STATUS = 'Failed' as const;

/**
 * The earliest selectable report year. The Flutter Reporting screen hardcodes 2023 as the dropdown
 * floor (`_generateYearStrings(2023, DateTime.now().year)` in `report_screen.dart`) — Givmo has no
 * donation data before then, and the backend renders an empty report for a dataless period anyway.
 */
export const REPORTS_FIRST_YEAR = 2023;

/**
 * The selectable report years, ascending from `REPORTS_FIRST_YEAR` through `currentYear` (parity with
 * the Flutter dropdown). Clamped so a clock skewed before the floor still yields the floor year
 * rather than an empty list.
 */
export function reportYearOptions(currentYear: number): string[] {
  const last = Math.max(currentYear, REPORTS_FIRST_YEAR);
  return Array.from({ length: last - REPORTS_FIRST_YEAR + 1 }, (_, i) =>
    String(REPORTS_FIRST_YEAR + i),
  );
}

/**
 * The generate-report request body for a year: Jan 1 – Dec 31 as `yyyy-MM-dd` strings. Mirrors the
 * Flutter `ReportsCubit.generateReport` (`DateTime(year)` / `DateTime(year, 12, 31)` through
 * `ValuesConverter.formatDateForApi`) so the wire shape is byte-identical to the legacy webapp's.
 */
export function buildGenerateReportBody(year: number): StatisticsReportPayload {
  return { start_period: `${year}-01-01`, end_period: `${year}-12-31` };
}

/** A report is downloadable iff the backend reports it as COMPLETED (the artifact exists). */
export function isReportDownloadable(report: StatisticsReportOut): boolean {
  return report.status === COMPLETED_STATUS;
}

/**
 * A report is retryable iff it FAILED. Retry re-invokes the SAME generate endpoint with the report's
 * own period (EP-FIX1 ⑤): the backend's active-report dedup only matches PENDING/IN_PROGRESS rows, so
 * a Failed period is not "active" and re-requesting it enqueues a FRESH generation attempt — no new
 * backend work. (The old Failed row remains; a new Pending row for the same period joins the list.)
 */
export function isReportRetryable(report: StatisticsReportOut): boolean {
  return report.status === FAILED_STATUS;
}

/**
 * The generate-report body to RETRY a failed report: its own `start_period`/`end_period`, sent
 * verbatim so the retry targets exactly the failed period (the wire values are already the
 * `yyyy-MM-dd` shape `StatisticsReportPayload` expects; see `buildGenerateReportBody`).
 */
export function buildRetryReportBody(report: StatisticsReportOut): StatisticsReportPayload {
  return { start_period: report.start_period, end_period: report.end_period };
}

/**
 * Human-readable reporting period — `start_period – end_period`, each rendered as a date-only string
 * when parseable, otherwise the raw value verbatim (never crash a row on an odd period string).
 */
export function reportPeriodLabel(report: StatisticsReportOut): string {
  const start = toDateOnly(report.start_period) || report.start_period || '';
  const end = toDateOnly(report.end_period) || report.end_period || '';
  if (start && end) return `${start} – ${end}`;
  return start || end || 'Report';
}

/**
 * A filesystem-safe download filename for a report PDF, derived from its period (e.g.
 * `givmo-report-2025-01-01_2025-12-31.pdf`). Used as the fallback when the response carries no
 * Content-Disposition filename.
 */
export function reportFilename(report: StatisticsReportOut): string {
  const start = toDateOnly(report.start_period) || report.start_period || '';
  const end = toDateOnly(report.end_period) || report.end_period || '';
  const period = [start, end]
    .filter(Boolean)
    .join('_')
    .replace(/[^A-Za-z0-9._-]/g, '-');
  const slug = period.length > 0 ? period : report.id;
  return `givmo-report-${slug}.pdf`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `yyyy-MM-dd` → "Jun 30", read off the string (never a `Date`, which shifts the day by zone). */
function shortDay(day: string): string | null {
  const match = /^\d{4}-(\d{2})-(\d{2})$/.exec(day);
  const month = match ? MONTHS[Number(match[1]) - 1] : undefined;
  return match && month ? `${month} ${Number(match[2])}` : null;
}

/**
 * A report's period in a person's words: "2025" for a calendar year (what the generate dialog asks
 * for), otherwise "Jan 1 – Jun 30, 2026". Falls back to {@link reportPeriodLabel} on an odd period.
 */
export function reportPeriodShortLabel(report: StatisticsReportOut): string {
  const start = toDateOnly(report.start_period) || '';
  const end = toDateOnly(report.end_period) || '';
  const year = start.slice(0, 4);
  if (/^\d{4}-01-01$/.test(start) && end === `${year}-12-31`) return year;
  const from = shortDay(start);
  const to = shortDay(end);
  if (from === null || to === null) return reportPeriodLabel(report);
  return start.slice(0, 4) === end.slice(0, 4)
    ? `${from} – ${to}, ${end.slice(0, 4)}`
    : `${from}, ${start.slice(0, 4)} – ${to}, ${end.slice(0, 4)}`;
}

/** The statuses of a report the render worker has not finished. */
const GENERATING_STATUSES: readonly string[] = ['Pending', 'In Progress'];

/**
 * The annual reports' state in one phrase, for the Reports page's Tax documents tile (EP-WEB26): the
 * newest READY report ("2025 report ready"); else the newest one still generating ("2026 report
 * generating" — report generation is a slow pipeline, and a slow pipeline shows its status); else
 * "None generated yet". `null` when the list holds only failed reports: the tile then states its
 * format alone, and the list itself (and its Retry) says what failed.
 */
export function taxDocumentsSummary(reports: readonly StatisticsReportOut[]): string | null {
  const newest = (rows: StatisticsReportOut[]) =>
    rows.reduce<StatisticsReportOut | undefined>(
      (best, row) =>
        best === undefined || String(row.end_period) > String(best.end_period) ? row : best,
      undefined,
    );
  const ready = newest(reports.filter(isReportDownloadable));
  if (ready !== undefined) return `${reportPeriodShortLabel(ready)} report ready`;
  const generating = newest(reports.filter((row) => GENERATING_STATUSES.includes(row.status)));
  if (generating !== undefined) return `${reportPeriodShortLabel(generating)} report generating`;
  return reports.length === 0 ? 'None generated yet' : null;
}
